import { ref, computed, watch, type Ref } from 'vue'
import { useBuilderPrefsStore, type HistoryFilterSnapshot } from '@/stores/builder-prefs-store'

export type RunStatus = 'Succeeded' | 'Failed' | 'Warning' | 'Pending'
export type TimeShortcut = '1h' | '1d' | '7d' | '30d' | 'custom'

export const ALL_RUN_STATUSES: readonly RunStatus[] = ['Succeeded', 'Failed', 'Warning', 'Pending'] as const
export const DEFAULT_TIME_SHORTCUT: TimeShortcut = '1d'
export const DEFAULT_PAGE_SIZE = 50
export const ALLOWED_PAGE_SIZES = [10, 25, 50, 100] as const

export interface ServerFilterParams {
  searchTerm: string
  /** Empty array means "no status filter" (i.e. all statuses). */
  statuses: RunStatus[]
  startedAtMin: Date | null
  startedAtMax: Date | null
  take: number
  /** Offset = page * take. Drives Cosmos OFFSET/LIMIT server-side (ADR-007). */
  skip: number
  /** Server-side sort token: 'newest' | 'oldest' | 'duration'. */
  sortBy: string
  /** Single-workflow narrowing (project scope); null = all workflows. Applied server-side so it
   *  filters the whole result set, not just the current page. */
  workflowId: string | null
  /** Single-product narrowing (org-wide Run History scope); null = all products. Applied server-side. */
  productId: string | null
  /** Grouped view: list root runs only, so children show only nested under their parent. App-wide
   *  pref (builder-prefs store), applied server-side (RootsOnly). */
  grouped: boolean
}

// Convert a time shortcut into a "min started-at" timestamp. Returns null for 'custom'
// (the caller should fall back to historyDateFrom/historyDateTo).
function shortcutToMinTs(shortcut: TimeShortcut, now: number): number | null {
  switch (shortcut) {
    case '1h':  return now - 60 * 60 * 1000
    case '1d':  return now - 24 * 60 * 60 * 1000
    case '7d':  return now - 7 * 24 * 60 * 60 * 1000
    case '30d': return now - 30 * 24 * 60 * 60 * 1000
    case 'custom': return null
  }
}

/**
 * Persistence context. When supplied, the composable reads the previously-saved
 * filter snapshot for the given id on mount (and whenever the id changes), and
 * writes the snapshot back to the builder-prefs store whenever any filter
 * changes. Omit to get a non-persisted instance.
 */
export interface HistoryFiltersPersistence {
  scope: 'project' | 'workflow'
  id: Ref<string>
}

export function useHistoryFilters(persistence?: HistoryFiltersPersistence) {
  // Grouped is an app-wide view pref (not a per-context snapshot filter), so read the store here —
  // every instance, persisted or not, folds the same pref into its server params.
  const prefs = useBuilderPrefsStore()

  const historySearchQuery = ref("")
  const historyStatusFilters = ref<RunStatus[]>([...ALL_RUN_STATUSES])
  const historyTimeShortcut = ref<TimeShortcut>(DEFAULT_TIME_SHORTCUT)
  const historyDateFrom = ref<Date | null>(null)
  const historyDateTo = ref<Date | null>(null)
  // Workflow filter (project scope) — workflow id or 'all'. Applied server-side (ADR-007).
  const historyWorkflowFilter = ref("all")
  // Product filter (org-wide Run History scope) — product id or 'all'. Applied server-side.
  const historyProductFilter = ref("all")
  const historySortBy = ref("newest") // 'newest', 'oldest', 'duration'
  const historyPageSize = ref<number>(DEFAULT_PAGE_SIZE)
  // Current page, 0-based. Reset to 0 whenever any other filter changes (a filtered set has a
  // different shape, so the old page index is meaningless). Deliberately not persisted.
  const historyPage = ref(0)

  // Back-compat shim for legacy callers that expect a single status string ('all' or one status).
  const historyStatusFilter = computed<string>(() => {
    if (historyStatusFilters.value.length === ALL_RUN_STATUSES.length) return 'all'
    if (historyStatusFilters.value.length === 1) return historyStatusFilters.value[0]
    return 'all'
  })

  /**
   * True when any setting hidden inside the "Advanced" section differs from its
   * default. Used to put an indicator dot on the gear toggle so the user can
   * tell at a glance that a filter is active even when advanced is collapsed.
   */
  const advancedFiltersDirty = computed(() =>
    historyTimeShortcut.value === 'custom' ||
    historyPageSize.value !== DEFAULT_PAGE_SIZE
  )

  /**
   * Snapshot of the filter state in the shape the API expects. Used as the
   * queryKey for useProjectRuns / useWorkflowRuns so changes trigger refetch.
   */
  const serverFilterParams = computed<ServerFilterParams>(() => {
    const statuses = historyStatusFilters.value
    // Only send a status filter when the user has deselected at least one. A full
    // selection means "all", which on the server is "no filter".
    const statusesToSend =
      statuses.length === ALL_RUN_STATUSES.length ? [] : [...statuses]

    let startedAtMin: Date | null = null
    let startedAtMax: Date | null = null
    if (historyTimeShortcut.value === 'custom') {
      startedAtMin = historyDateFrom.value
      if (historyDateTo.value) {
        // End-of-day inclusive on the "to" date.
        const d = new Date(historyDateTo.value)
        d.setHours(23, 59, 59, 999)
        startedAtMax = d
      }
    } else {
      const minTs = shortcutToMinTs(historyTimeShortcut.value, Date.now())
      if (minTs !== null) startedAtMin = new Date(minTs)
    }

    return {
      searchTerm: historySearchQuery.value.trim(),
      statuses: statusesToSend,
      startedAtMin,
      startedAtMax,
      take: historyPageSize.value,
      skip: historyPage.value * historyPageSize.value,
      sortBy: historySortBy.value,
      workflowId:
        historyWorkflowFilter.value && historyWorkflowFilter.value !== 'all'
          ? historyWorkflowFilter.value
          : null,
      productId:
        historyProductFilter.value && historyProductFilter.value !== 'all'
          ? historyProductFilter.value
          : null,
      grouped: prefs.groupChildRuns,
    }
  })

  // Any filter change invalidates the current page index — snap back to the first page so the
  // user never lands on an out-of-range page (e.g. was on page 5, narrowed to 1 page of results).
  watch(
    [
      historySearchQuery,
      historyStatusFilters,
      historyTimeShortcut,
      historyDateFrom,
      historyDateTo,
      historyWorkflowFilter,
      historyProductFilter,
      historySortBy,
      historyPageSize,
    ],
    () => { historyPage.value = 0 },
    { deep: true }
  )

  function resetHistoryFilters() {
    historySearchQuery.value = ""
    historyStatusFilters.value = [...ALL_RUN_STATUSES]
    historyTimeShortcut.value = DEFAULT_TIME_SHORTCUT
    historyDateFrom.value = null
    historyDateTo.value = null
    historyWorkflowFilter.value = "all"
    historyProductFilter.value = "all"
    historySortBy.value = "newest"
    historyPageSize.value = DEFAULT_PAGE_SIZE
    historyPage.value = 0
  }

  function updatePage(page: number) {
    historyPage.value = Math.max(0, page)
  }

  function updateSearchQuery(query: string) {
    historySearchQuery.value = query
  }

  function updateStatusFilter(status: string) {
    if (status === 'all') {
      historyStatusFilters.value = [...ALL_RUN_STATUSES]
    } else {
      historyStatusFilters.value = [status as RunStatus]
    }
  }

  function updateStatusFilters(statuses: RunStatus[]) {
    historyStatusFilters.value = statuses
  }

  // Toggle a single status. Refuses to drop the last selected one — instead it
  // wraps around to "all selected" so the user can never end up with zero
  // statuses (which would silently filter out everything).
  function toggleStatusFilter(status: RunStatus) {
    const current = historyStatusFilters.value
    if (current.includes(status)) {
      const remaining = current.filter((s) => s !== status)
      historyStatusFilters.value = remaining.length === 0 ? [...ALL_RUN_STATUSES] : remaining
    } else {
      historyStatusFilters.value = [...current, status]
    }
  }

  function updateTimeShortcut(shortcut: TimeShortcut) {
    historyTimeShortcut.value = shortcut
    if (shortcut !== 'custom') {
      historyDateFrom.value = null
      historyDateTo.value = null
    }
  }

  function updateDateRange(from: Date | null, to: Date | null) {
    historyDateFrom.value = from
    historyDateTo.value = to
    if (from || to) historyTimeShortcut.value = 'custom'
  }

  function updateWorkflowFilter(workflowId: string) {
    historyWorkflowFilter.value = workflowId
  }

  function updateProductFilter(productId: string) {
    historyProductFilter.value = productId
  }

  function updateSortBy(sortBy: string) {
    historySortBy.value = sortBy
  }

  function updatePageSize(size: number) {
    historyPageSize.value = size
  }

  // --- Persistence ----------------------------------------------------------
  if (persistence) {
    // Capture into a local const so TypeScript carries the non-undefined narrowing
    // into the watch callbacks below.
    const pers = persistence
    const store = prefs

    function loadSnapshotForId(id: string) {
      if (!id) return
      const snap =
        pers.scope === 'project'
          ? store.projectHistoryFiltersFor(id)
          : store.workflowHistoryFiltersFor(id)
      if (!snap) {
        // No saved state — reset to defaults so a fresh project/workflow
        // doesn't inherit whatever the user had configured elsewhere.
        historySearchQuery.value = ''
        historyStatusFilters.value = [...ALL_RUN_STATUSES]
        historyTimeShortcut.value = DEFAULT_TIME_SHORTCUT
        historyDateFrom.value = null
        historyDateTo.value = null
        historyWorkflowFilter.value = 'all'
        historySortBy.value = 'newest'
        historyPageSize.value = DEFAULT_PAGE_SIZE
        return
      }
      historySearchQuery.value = snap.searchQuery
      historyStatusFilters.value = snap.statusFilters.filter((s): s is RunStatus =>
        (ALL_RUN_STATUSES as readonly string[]).includes(s)
      )
      // Drop stale custom date ranges on load — see store comment.
      const isCustom = snap.timeShortcut === 'custom'
      historyTimeShortcut.value = (isCustom ? DEFAULT_TIME_SHORTCUT : snap.timeShortcut) as TimeShortcut
      historyDateFrom.value = null
      historyDateTo.value = null
      historyWorkflowFilter.value = snap.workflowFilter
      historySortBy.value = snap.sortBy
      historyPageSize.value = snap.pageSize
    }

    // Hydrate now + whenever the id changes (route navigation between projects /
    // workflows). The write-back watcher below will then immediately re-persist
    // the loaded values to the new id's slot — harmless since it's the same data.
    watch(pers.id, (id) => loadSnapshotForId(id), { immediate: true })

    // Persist on any filter change. Skips when id is empty (still mid-route).
    watch(
      [
        historySearchQuery,
        historyStatusFilters,
        historyTimeShortcut,
        historyDateFrom,
        historyDateTo,
        historyWorkflowFilter,
        historySortBy,
        historyPageSize,
      ],
      () => {
        const id = pers.id.value
        if (!id) return
        const snapshot: HistoryFilterSnapshot = {
          searchQuery: historySearchQuery.value,
          statusFilters: [...historyStatusFilters.value],
          timeShortcut: historyTimeShortcut.value,
          dateFromIso: historyDateFrom.value?.toISOString() ?? null,
          dateToIso: historyDateTo.value?.toISOString() ?? null,
          workflowFilter: historyWorkflowFilter.value,
          sortBy: historySortBy.value,
          pageSize: historyPageSize.value,
        }
        if (pers.scope === 'project') {
          store.setProjectHistoryFilters(id, snapshot)
        } else {
          store.setWorkflowHistoryFilters(id, snapshot)
        }
      },
      { deep: true }
    )
  }

  return {
    // State
    historySearchQuery,
    historyStatusFilter,
    historyStatusFilters,
    historyTimeShortcut,
    historyDateFrom,
    historyDateTo,
    historyWorkflowFilter,
    historyProductFilter,
    historySortBy,
    historyPageSize,
    historyPage,

    // Computed
    serverFilterParams,
    advancedFiltersDirty,

    // Methods
    resetHistoryFilters,
    updateSearchQuery,
    updateStatusFilter,
    updateStatusFilters,
    toggleStatusFilter,
    updateTimeShortcut,
    updateDateRange,
    updateWorkflowFilter,
    updateProductFilter,
    updateSortBy,
    updatePageSize,
    updatePage,
  }
}

export type HistoryFilters = ReturnType<typeof useHistoryFilters>

/**
 * Serialize the server-side filter params into a query string suitable for
 * appending to the run-history endpoints. Empty fields are omitted; the status
 * array is repeated as multiple `statuses=` params to bind to .NET `Status[]`.
 */
export function serverFilterParamsToQueryString(params: ServerFilterParams): string {
  const qs = new URLSearchParams()
  if (params.searchTerm) qs.set('searchTerm', params.searchTerm)
  for (const status of params.statuses) qs.append('statuses', status)
  if (params.startedAtMin) qs.set('startedAtMin', params.startedAtMin.toISOString())
  if (params.startedAtMax) qs.set('startedAtMax', params.startedAtMax.toISOString())
  qs.set('take', String(params.take))
  if (params.skip > 0) qs.set('skip', String(params.skip))
  if (params.sortBy && params.sortBy !== 'newest') qs.set('sortBy', params.sortBy)
  if (params.workflowId) qs.set('workflowId', params.workflowId)
  if (params.productId) qs.set('productId', params.productId)
  if (params.grouped) qs.set('grouped', 'true')
  return qs.toString()
}
